import { useEffect, useRef, useState } from 'react';
import { collection, doc, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  uploadDocument, verifyAuditChain, getDocumentUrl, getCertificateUrl, removeDocument, replaceDocument,
} from '../lib/api';
import { formatBytes, type AuditEvent, type DocumentRecord } from '@beckersign/shared';
import { friendlyError } from '../lib/errors';
import CanvasEditor from '../canvas/CanvasEditor';
import RolesEditor, { type Role } from '../components/RolesEditor';
import RecipientsEditor from '../components/RecipientsEditor';
import SendPanel from '../components/SendPanel';
import type { Recipient } from '@beckersign/shared';

interface Props { envelopeId: string; onBack: () => void }

export default function EnvelopeDetail({ envelopeId, onBack }: Props) {
  const [envelope, setEnvelope] = useState<Record<string, unknown> | null>(null);
  const [docs, setDocs] = useState<DocumentRecord[]>([]);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [pct, setPct] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [replacing, setReplacing] = useState<string | null>(null);
  const [chain, setChain] = useState<{ valid: boolean; eventCount: number; problems: string[] } | null>(null);
  const [tab, setTab] = useState<'documents' | 'fields' | 'recipients' | 'audit'>('documents');
  const [fieldMeta, setFieldMeta] = useState<{ roleId: string; required: boolean }[]>([]);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const replaceInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const u1 = onSnapshot(doc(db, 'envelopes', envelopeId), (s) => setEnvelope(s.data() ?? null));
    const u2 = onSnapshot(
      query(collection(db, 'envelopes', envelopeId, 'documents'), orderBy('order')),
      (s) => setDocs(s.docs.map((d) => d.data() as DocumentRecord))
    );
    const u3 = onSnapshot(
      query(collection(db, 'envelopes', envelopeId, 'events'), orderBy('seq')),
      (s) => setEvents(s.docs.map((d) => d.data() as AuditEvent))
    );
    const u4 = onSnapshot(collection(db, 'envelopes', envelopeId, 'fields'), (s) =>
      setFieldMeta(s.docs.map((d2) => d2.data() as { roleId: string; required: boolean })));
    const u5 = onSnapshot(collection(db, 'envelopes', envelopeId, 'recipients'), (s) =>
      setRecipients(s.docs.map((d2) => d2.data() as Recipient)));
    return () => { u1(); u2(); u3(); u4(); u5(); };
  }, [envelopeId]);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(null); setPct(0);
    try {
      await uploadDocument(envelopeId, file, setPct);
    } catch (ex) {
      setErr(friendlyError(ex, 'Upload failed.'));
    } finally {
      setPct(null);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function onRemove(d: DocumentRecord) {
    if (!window.confirm(`Remove "${d.name}" from this envelope? Fields placed on it are deleted too.`)) return;
    setErr(null); setNote(null);
    try {
      await removeDocument({ envelopeId, documentId: d.id });
    } catch (ex) {
      setErr(friendlyError(ex, 'Could not remove the document.'));
    }
  }

  async function onReplaceFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    const documentId = replacing;
    if (!file || !documentId) return;
    setErr(null); setNote(null); setPct(0);
    try {
      const { fieldsDropped } = await replaceDocument(envelopeId, documentId, file, setPct);
      if (fieldsDropped > 0) {
        setNote(`${fieldsDropped} field${fieldsDropped === 1 ? ' was' : 's were'} on pages the new file doesn't have and ${fieldsDropped === 1 ? 'was' : 'were'} removed.`);
      }
    } catch (ex) {
      setErr(friendlyError(ex, 'Could not replace the document.'));
    } finally {
      setPct(null); setReplacing(null);
      if (replaceInput.current) replaceInput.current.value = '';
    }
  }

  async function openDoc(documentId: string, kind: 'original' | 'final' = 'original') {
    setErr(null);
    try {
      const res = await getDocumentUrl({ envelopeId, documentId, kind });
      window.open(res.data.url, '_blank', 'noopener');
    } catch (e) {
      setErr(friendlyError(e, 'Could not open that document.'));
    }
  }

  async function openCertificate() {
    setErr(null);
    try {
      const res = await getCertificateUrl({ envelopeId });
      window.open(res.data.url, '_blank', 'noopener');
    } catch (e) {
      setErr(friendlyError(e, 'Could not open the certificate.'));
    }
  }

  /** C-36: the audit trail must be exportable as machine-readable JSON. */
  function exportAuditJson() {
    const payload = {
      envelopeId,
      envelopeName: envelope?.name ?? '',
      status: envelope?.status ?? '',
      exportedAt: new Date().toISOString(),
      note: 'Each event carries prevEventHash and hash. Recompute SHA-256 over the canonical JSON of {envelopeId, seq, type, at, actor, actorDevice, ip, userAgent, payload, prevEventHash} to verify the chain independently of BeckerSign.',
      documents: docs.map((d) => ({
        id: d.id, name: d.name, pageCount: d.pageCount,
        sha256AtUpload: d.originalSha256,
      })),
      recipients,
      events,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `beckersign-audit-${envelopeId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function onVerify() {
    setChain(null);
    const res = await verifyAuditChain({ envelopeId });
    setChain(res.data);
  }

  const roles: Role[] = Array.isArray(envelope?.roles) && (envelope.roles as Role[]).length > 0
    ? (envelope.roles as Role[])
    : [{ roleId: 'role1', name: 'Signer 1' }];
  const isDraft = envelope?.status === 'draft';

  return (
    <>
      <div className="row">
        <button onClick={onBack}>← All envelopes</button>
        <code className="muted">{envelopeId}</code>
      </div>

      <h1 style={{ marginTop: '1rem' }}>{String(envelope?.name ?? '…')}</h1>
      <p className="muted">
        Status: <code>{String(envelope?.status ?? '')}</code>
        {envelope?.documentCategoryLabel ? <> · {String(envelope.documentCategoryLabel)}</> : null}
      </p>

      <div className="row" style={{ gap: '.4rem', margin: '1rem 0', justifyContent: 'flex-start' }}>
        {(['documents', 'fields', 'recipients', 'audit'] as const).map((t2) => (
          <button
            key={t2} onClick={() => setTab(t2)}
            aria-current={tab === t2}
            style={tab === t2
              ? { background: 'var(--accent)', color: 'var(--accent-fg)', borderColor: 'var(--accent)' }
              : undefined}
          >
            {t2 === 'documents' ? 'Documents' : t2 === 'fields' ? 'Place fields' : t2 === 'recipients' ? 'Recipients' : 'Audit trail'}
          </button>
        ))}
      </div>

      {tab === 'fields' && (
        <>
          <RolesEditor envelopeId={envelopeId} roles={roles} readOnly={!isDraft} />
          <h2>Place fields</h2>
          <CanvasEditor
            envelopeId={envelopeId} documents={docs} roles={roles} readOnly={!isDraft}
          />
        </>
      )}

      {tab === 'documents' && <>
      {envelope?.status === 'completed' && (
        <div className="notice" style={{ borderColor: 'var(--ok)' }}>
          <strong>Signing complete.</strong>{' '}
          {envelope?.certificatePath
            ? 'The signed documents and Certificate of Completion have been generated and emailed to every party.'
            : 'Generating the signed documents and certificate…'}
          {envelope?.certificatePath ? (
            <div className="row" style={{ gap: '.4rem', marginTop: '.6rem', justifyContent: 'flex-start' }}>
              <button onClick={() => void openCertificate()}>Certificate of Completion</button>
            </div>
          ) : null}
        </div>
      )}

      <h2>Recipient progress</h2>
      {recipients.length === 0 ? (
        <p className="muted">No recipients yet.</p>
      ) : (
        <table>
          <thead><tr><th>Name</th><th>Email</th><th>Order</th><th>Status</th><th>Signed</th></tr></thead>
          <tbody>
            {recipients.slice().sort((a, b) => a.routingOrder - b.routingOrder).map((r) => (
              <tr key={r.id}>
                <td>{r.name}</td>
                <td className="muted" style={{ fontSize: '.85rem' }}>{r.email}</td>
                <td>{r.routingOrder}</td>
                <td><code>{r.status}</code></td>
                <td className="muted" style={{ fontSize: '.8rem' }}>
                  {r.completedAt ? new Date(r.completedAt).toLocaleString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2>Documents</h2>
      <p className="muted">
        PDF only, 25 MB per file, 10 per envelope. The original is stored byte-for-byte and never
        modified; everything generated later is written alongside it, never over it. Until the
        envelope is sent you can replace or remove a document; replacing keeps its fields.
      </p>
      <input
        ref={fileInput} type="file" accept="application/pdf,.pdf"
        onChange={onFile} disabled={pct !== null || envelope?.status !== 'draft'}
      />
      {pct !== null && <p className="muted">Uploading… {pct}%{pct === 100 ? ' — validating and hashing' : ''}</p>}
      <input
        ref={replaceInput} type="file" accept="application/pdf,.pdf" hidden
        onChange={onReplaceFile}
      />
      {err && <div className="alert" role="alert">{err}</div>}
      {note && <div className="notice" role="status">{note}</div>}

      {docs.length > 0 && (
        <table>
          <thead><tr><th>Name</th><th>Pages</th><th>Size</th><th>SHA-256 (at upload)</th><th /></tr></thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d.id}>
                <td>{d.name}</td>
                <td>{d.pageCount}</td>
                <td>{formatBytes(d.sizeBytes)}</td>
                <td><code style={{ wordBreak: 'break-all', fontSize: '.75rem' }}>{d.originalSha256}</code></td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button onClick={() => void openDoc(d.id, 'original')}>Original</button>
                  {(d as { finalPath?: string }).finalPath && (
                    <button onClick={() => void openDoc(d.id, 'final')} style={{ marginLeft: '.3rem' }}>
                      Signed
                    </button>
                  )}
                  {isDraft && (
                    <>
                      <button
                        onClick={() => { setReplacing(d.id); replaceInput.current?.click(); }}
                        disabled={pct !== null} style={{ marginLeft: '.3rem' }}
                      >Replace</button>
                      <button
                        onClick={() => void onRemove(d)}
                        disabled={pct !== null} style={{ marginLeft: '.3rem' }}
                      >Remove</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      </>}

      {tab === 'recipients' && (
        <>
          <RecipientsEditor
            envelopeId={envelopeId} roles={roles} fields={fieldMeta}
            documentCount={docs.length} readOnly={!isDraft}
          />
          <hr style={{ margin: '2rem 0', border: 0, borderTop: '1px solid var(--line)' }} />
          <SendPanel
            envelopeId={envelopeId} envelopeName={String(envelope?.name ?? '')}
            recipients={recipients} roles={roles} fields={fieldMeta}
            documentCount={docs.length} status={String(envelope?.status ?? 'draft')}
          />
        </>
      )}

      {tab === 'audit' && <>
      <h2>Audit trail</h2>
      <div className="row" style={{ marginBottom: '.75rem' }}>
        <p className="muted" style={{ margin: 0 }}>
          Append-only. Every event is hash-chained to the one before it.
        </p>
        <div className="row" style={{ gap: '.4rem' }}>
          <button onClick={() => void onVerify()}>Verify chain</button>
          <button onClick={exportAuditJson}>Export JSON</button>
        </div>
      </div>
      {chain && (
        <div className={chain.valid ? 'notice' : 'alert'} role="status">
          {chain.valid
            ? `✓ Chain intact across ${chain.eventCount} events. Every hash recomputes and every link matches.`
            : `✗ Chain BROKEN: ${chain.problems.join('; ')}`}
        </div>
      )}
      <table>
        <thead><tr><th>#</th><th>Event</th><th>When (UTC)</th><th>Actor</th><th>Hash</th></tr></thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.seq}>
              <td>{e.seq}</td>
              <td><code>{e.type}</code>{Object.keys(e.payload ?? {}).length > 0 && (
                <div className="muted" style={{ fontSize: '.75rem' }}>
                  {Object.entries(e.payload).map(([k, v]) => `${k}=${String(v)}`).join('  ')}
                </div>
              )}</td>
              <td className="muted" style={{ fontSize: '.8rem' }}>{e.at}</td>
              <td className="muted" style={{ fontSize: '.8rem' }}>{e.actor?.kind}</td>
              <td><code style={{ fontSize: '.7rem' }}>{e.hash?.slice(0, 12)}…</code></td>
            </tr>
          ))}
        </tbody>
      </table>
      </>}
    </>
  );
}
